/**
 * LevNutri — Dashboard Principal
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell,
  LineChart, Line,
  ResponsiveContainer,
} from 'recharts';
import {
  BarChart3, PieChart as PieIcon, TrendingUp, Cake, ClipboardList, CheckCircle2, CalendarDays,
  Calendar, Clock, Target, MessageCircle, Plus, Users, User, AlertCircle, Building2, Laptop, Inbox,
} from 'lucide-react';
import { dashboardService, pendenciasService, leadsService, saldoAlertasService } from '../services/api';
import { emo } from './ui/Ico';

// Ícones de linha: ic = ao lado de texto; icn = sozinho
const ic  = (Cmp, size = 15) => <Cmp size={size} strokeWidth={1.75} style={{ verticalAlign: '-0.18em', marginRight: 6, flexShrink: 0 }} />;
const icn = (Cmp, size = 18) => <Cmp size={size} strokeWidth={1.75} style={{ flexShrink: 0 }} />;

// ── Brand palette ─────────────────────────────────────────────────────────────
const C = {
  primary:  '#d4a574',
  dark:     '#5a3e2b',
  darker:   '#2f2a26',
  bg:       '#fbf8f5',
  card:     '#ffffff',
  border:   '#ebe3da',
  green:    '#736b64',
  yellow:   '#b8895c',
  red:      '#c46a60',
  blue:     '#7189a6',
  purple:   '#7189a6',
};

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://levnutri.com/nutri';

const PT_MONTHS = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const PT_WEEKDAYS = ['domingo','segunda','terça','quarta','quinta','sexta','sábado'];
const OBJ_LABELS = {
  perda_peso:   'Perda de Peso',
  ganho_massa:  'Ganho de Massa',
  ganho_peso:   'Ganho de Peso',
  manutencao:   'Manutenção',
  saude_geral:  'Saúde Geral',
  performance:  'Performance',
};
const OBJ_COLORS = [C.primary, C.blue, '#6f9a77', '#e8c9a8', C.purple, '#d4a574', C.yellow];

function fmtMonth(ym) {
  if (!ym) return '';
  const [y, m] = ym.split('-');
  return `${PT_MONTHS[parseInt(m, 10) - 1]}/${y.slice(2)}`;
}

function fmtBRL(v) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
}

// Recebe string MySQL "2024-06-15 09:30:00" → Date
function parseDateTime(str) {
  if (!str) return null;
  return new Date(String(str).replace(' ', 'T'));
}

function fmtTime(str) {
  const d = parseDateTime(str);
  if (!d) return '';
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function fmtDateFull(str) {
  const d = parseDateTime(str);
  if (!d) return '';
  return d.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long' });
}

function fmtDateShort(str) {
  const d = parseDateTime(str);
  if (!d) return '';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

function isToday(str) {
  const d = parseDateTime(str);
  if (!d) return false;
  const today = new Date();
  return d.toDateString() === today.toDateString();
}

function isTomorrow(str) {
  const d = parseDateTime(str);
  if (!d) return false;
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return d.toDateString() === tomorrow.toDateString();
}

function relativeDayLabel(str) {
  if (isToday(str)) return 'Hoje';
  if (isTomorrow(str)) return 'Amanhã';
  return fmtDateShort(str);
}

// ── Base card ─────────────────────────────────────────────────────────────────
function Card({ children, style }) {
  return (
    <div style={{
      background: C.card, borderRadius: '12px', padding: '1.25rem',
      border: `1px solid ${C.border}`, boxShadow: '0 1px 4px rgba(0,0,0,.05)',
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── Section label ─────────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <p style={{
      margin: '0 0 0.6rem',
      fontSize: '0.7rem', fontWeight: 600,
      color: '#736b64', textTransform: 'uppercase', letterSpacing: '.1em',
    }}>
      {children}
    </p>
  );
}

// ── Summary card ─────────────────────────────────────────────────────────────
function SummaryCard({ icon, label, value, sub, color, progress }) {
  return (
    <Card style={{ flex: 1, minWidth: '170px' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{
          width: 40, height: 40, borderRadius: '10px', flexShrink: 0,
          background: color + '18',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '1.15rem', color,
        }}>
          {emo(icon)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: '0 0 0.1rem', fontSize: '0.72rem', fontWeight: 600, color: '#736b64', textTransform: 'uppercase', letterSpacing: '.04em' }}>
            {emo(label)}
          </p>
          <p style={{ margin: 0, fontSize: '1.3rem', fontWeight: 700, color: C.darker, lineHeight: 1.15 }}>
            {value}
          </p>
          {sub && (
            <p style={{ margin: '0.15rem 0 0', fontSize: '0.73rem', color: '#736b64' }}>{sub}</p>
          )}
        </div>
      </div>
      {progress !== undefined && (
        <div style={{ marginTop: '0.75rem' }}>
          <div style={{ height: 5, background: '#f6f0ea', borderRadius: '999px', overflow: 'hidden' }}>
            <div style={{
              height: '100%', borderRadius: '999px',
              width: `${Math.min(progress, 100)}%`,
              background: progress >= 100 ? C.green : C.primary,
              transition: 'width .5s ease',
            }} />
          </div>
          <p style={{ margin: '0.2rem 0 0', fontSize: '0.7rem', color: '#736b64', textAlign: 'right' }}>
            {Math.round(progress)}% da meta
          </p>
        </div>
      )}
    </Card>
  );
}

// ── Próxima Consulta — card de destaque ───────────────────────────────────────
function ProximaConsultaCard({ consulta, onNavigate }) {
  if (!consulta) {
    return (
      <Card style={{
        background: 'linear-gradient(135deg, #fbf8f5 0%, #f5ece2 100%)',
        border: `1.5px dashed ${C.primary}40`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '140px', gap: '0.75rem', textAlign: 'center',
      }}>
        <span style={{ opacity: .35, color: C.dark }}>{icn(Calendar, 32)}</span>
        <p style={{ margin: 0, color: '#736b64', fontSize: '0.9rem' }}>Nenhuma consulta agendada</p>
        <button
          onClick={() => onNavigate('agenda')}
          style={{ padding: '0.45rem 1.1rem', background: C.primary, color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.83rem' }}
        >
          {ic(Calendar)}Agendar
        </button>
      </Card>
    );
  }

  const hoje = isToday(consulta.data_hora);
  const labelDia = hoje ? 'Hoje' : isTomorrow(consulta.data_hora) ? 'Amanhã' : fmtDateFull(consulta.data_hora);
  const initials = (consulta.patient_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
  const tipoColor = consulta.tipo === 'online' ? C.blue : C.primary;
  const tipoBg    = consulta.tipo === 'online' ? '#edf1f6' : '#fbf8f5';
  const tipoLabel = consulta.tipo === 'online' ? <>{ic(Laptop, 13)}Online</> : <>{ic(Building2, 13)}Presencial</>;

  return (
    <Card style={{
      background: hoje
        ? 'linear-gradient(135deg, #fbf8f5 0%, #f6f0ea 100%)'
        : 'linear-gradient(135deg, #edf1f6 0%, #edf1f6 100%)',
      border: `1.5px solid ${hoje ? C.primary + '50' : C.purple + '40'}`,
      position: 'relative', overflow: 'hidden',
    }}>
      {/* Accent stripe */}
      <div style={{
        position: 'absolute', top: 0, left: 0, bottom: 0, width: '4px',
        background: hoje
          ? `linear-gradient(180deg, ${C.primary}, #e8c9a8)`
          : `linear-gradient(180deg, ${C.purple}, #b8c7d8)`,
        borderRadius: '12px 0 0 12px',
      }} />

      <div style={{ paddingLeft: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
          <div>
            <p style={{ margin: '0 0 0.15rem', fontSize: '0.68rem', fontWeight: 600, color: '#736b64', textTransform: 'uppercase', letterSpacing: '.07em' }}>
              Próxima Consulta
            </p>
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: hoje ? C.dark : '#4f6a86', textTransform: 'capitalize' }}>
              {labelDia}
            </p>
          </div>
          <span style={{ padding: '0.2rem 0.65rem', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 600, background: tipoBg, color: tipoColor, border: `1px solid ${tipoColor}30` }}>
            {emo(tipoLabel)}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.85rem' }}>
          <div style={{
            width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
            background: hoje ? `linear-gradient(135deg, ${C.primary}, #e8c9a8)` : `linear-gradient(135deg, ${C.purple}, #b8c7d8)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.9rem', fontWeight: 700, color: '#ffffff', overflow: 'hidden',
          }}>
            {consulta.foto_url
              ? <img src={`${API_BASE}${consulta.foto_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              : initials}
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700, color: C.darker }}>{consulta.patient_name}</p>
            <p style={{ margin: '0.1rem 0 0', fontSize: '0.82rem', color: '#736b64' }}>
              {ic(Clock, 13)}{fmtTime(consulta.data_hora)}
              {consulta.duracao_minutos && ` · ${consulta.duracao_minutos} min`}
            </p>
          </div>
        </div>

        <button
          onClick={() => onNavigate('patient-detail', consulta.patient_id)}
          style={{
            padding: '0.55rem 1.25rem',
            background: hoje ? `linear-gradient(135deg, ${C.primary}, #e8c9a8)` : `linear-gradient(135deg, ${C.purple}, #b8c7d8)`,
            color: '#ffffff', border: 'none', borderRadius: '8px',
            cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem',
          }}
        >
          Abrir Prontuário →
        </button>
      </div>
    </Card>
  );
}

// ── Meta do Mês ───────────────────────────────────────────────────────────────
function MetaMes({ fin, appt }) {
  const metaReceita   = fin?.meta_receita    || 3000;
  const metaConsultas = fin?.meta_consultas  || 20;
  // Usa receita de financial_records se disponível; fallback: valor_cobrado das consultas
  const receitaReal   = (fin?.receita || 0) > 0 ? fin.receita : (fin?.receita_consultas || 0);
  const consultasReal = appt?.total || 0;

  const pctReceita    = Math.min(100, metaReceita > 0 ? (receitaReal / metaReceita) * 100 : 0);
  const pctConsultas  = Math.min(100, metaConsultas > 0 ? (consultasReal / metaConsultas) * 100 : 0);

  const recColor = pctReceita >= 80 ? C.green : pctReceita >= 50 ? C.yellow : C.red;
  const conColor = pctConsultas >= 80 ? C.green : pctConsultas >= 50 ? C.yellow : C.red;

  const faltamConsultas = Math.max(0, metaConsultas - consultasReal);
  const metaBatida      = consultasReal >= metaConsultas;

  return (
    <Card>
      <p style={{ margin: '0 0 0.6rem', fontSize: '0.7rem', fontWeight: 600, color: '#736b64', textTransform: 'uppercase', letterSpacing: '.08em' }}>
        {ic(Target)}Meta do Mês
      </p>

      {/* Receita */}
      <div style={{ marginBottom: '0.75rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#736b64' }}>Receita</span>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: recColor }}>
            {Math.round(pctReceita)}%
          </span>
        </div>
        <div style={{ height: 6, background: '#f6f0ea', borderRadius: '999px', overflow: 'hidden', marginBottom: '0.2rem' }}>
          <div style={{ height: '100%', borderRadius: '999px', width: `${pctReceita}%`, background: recColor, transition: 'width .5s ease' }} />
        </div>
        <p style={{ margin: 0, fontSize: '0.72rem', color: '#736b64' }}>
          {fmtBRL(receitaReal)} de {fmtBRL(metaReceita)}
        </p>
      </div>

      {/* Consultas */}
      <div style={{ marginBottom: '0.6rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
          <span style={{ fontSize: '0.75rem', color: '#736b64' }}>Consultas realizadas</span>
          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: conColor }}>
            {consultasReal} / {metaConsultas}
          </span>
        </div>
        <div style={{ height: 6, background: '#f6f0ea', borderRadius: '999px', overflow: 'hidden' }}>
          <div style={{ height: '100%', borderRadius: '999px', width: `${pctConsultas}%`, background: conColor, transition: 'width .5s ease' }} />
        </div>
      </div>

      {/* Status final */}
      <div style={{ paddingTop: '0.5rem', borderTop: `1px solid ${C.border}` }}>
        {metaBatida ? (
          <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 600, color: C.green }}>
            {ic(CheckCircle2)}Meta de consultas atingida!
          </p>
        ) : (
          <p style={{ margin: 0, fontSize: '0.78rem', color: '#736b64' }}>
            Faltam <strong style={{ color: conColor }}>{faltamConsultas}</strong> consultas para bater a meta
          </p>
        )}
      </div>
    </Card>
  );
}

// ── Stats 30 dias ─────────────────────────────────────────────────────────────
function Stats30d({ stats }) {
  const items = [
    { icon: icn(User, 15), label: 'Novos pacientes',      value: stats?.novos_pacientes ?? 0,      color: C.green },
    { icon: icn(CheckCircle2, 15), label: 'Consultas realizadas', value: stats?.consultas_realizadas ?? 0, color: C.primary },
    { icon: icn(Calendar, 15), label: 'Agendamentos futuros', value: stats?.consultas_agendadas ?? 0,  color: C.blue },
    { icon: icn(ClipboardList, 15), label: 'Fichas aguardando',    value: stats?.fichas_aguardando ?? 0,    color: C.yellow },
  ];
  return (
    <Card style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      <p style={{ margin: '0 0 0.3rem', fontSize: '0.7rem', fontWeight: 600, color: '#736b64', textTransform: 'uppercase', letterSpacing: '.08em' }}>
        Últimos 30 dias
      </p>
      {items.map(({ icon, label, value, color }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0', borderBottom: `1px solid ${C.border}` }}>
          <div style={{ width: 30, height: 30, borderRadius: '8px', background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.9rem', flexShrink: 0, color }}>
            {emo(icon)}
          </div>
          <p style={{ margin: 0, flex: 1, fontSize: '0.82rem', color: '#4a4440' }}>{emo(label)}</p>
          <p style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: value > 0 ? color : '#d9d0c6' }}>{value}</p>
        </div>
      ))}
    </Card>
  );
}

// ── Próximas consultas da semana ──────────────────────────────────────────────
function ProximasConsultasList({ consultas, onNavigate }) {
  if (!consultas?.length) {
    return (
      <Card>
        <p style={{ margin: '0 0 0.75rem', fontWeight: 600, color: C.darker, fontSize: '0.88rem' }}>
          {ic(CalendarDays)}Próximas Consultas
        </p>
        <p style={{ color: '#d9d0c6', fontSize: '0.82rem', textAlign: 'center', padding: '0.75rem 0' }}>
          Nenhuma consulta agendada
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <p style={{ margin: '0 0 0.75rem', fontWeight: 600, color: C.darker, fontSize: '0.88rem' }}>
        {ic(CalendarDays)}Próximas Consultas
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
        {consultas.map((c, i) => {
          const initials = (c.patient_name || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
          const dayLabel = relativeDayLabel(c.data_hora);
          const hoje = isToday(c.data_hora);
          return (
            <div
              key={c.id || i}
              onClick={() => onNavigate('patient-detail', c.patient_id)}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.65rem',
                padding: '0.55rem 0', borderBottom: i < consultas.length - 1 ? `1px solid ${C.border}` : 'none',
                cursor: 'pointer',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#fbf8f5'; e.currentTarget.style.borderRadius = '8px'; }}
              onMouseLeave={e => { e.currentTarget.style.background = ''; }}
            >
              <div style={{
                width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                background: hoje ? `linear-gradient(135deg, ${C.primary}, #e8c9a8)` : C.primary + '20',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 600, color: hoje ? '#ffffff' : C.dark,
                overflow: 'hidden',
              }}>
                {c.foto_url
                  ? <img src={`${API_BASE}${c.foto_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '0.86rem', fontWeight: 600, color: C.darker, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.patient_name}
                </p>
                <p style={{ margin: 0, fontSize: '0.73rem', color: '#736b64' }}>
                  {fmtTime(c.data_hora)} · {dayLabel}
                </p>
              </div>
              <span style={{
                fontSize: '0.72rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: '20px',
                background: c.tipo === 'online' ? '#edf1f6' : '#fbf8f5',
                color: c.tipo === 'online' ? C.blue : C.primary,
              }}>
                {c.tipo === 'online' ? icn(Laptop, 14) : icn(Building2, 14)}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ── Alert patient row ─────────────────────────────────────────────────────────
function AlertPatient({ p, sub, onNavigate }) {
  return (
    <div
      onClick={() => onNavigate('patient-detail', p.id)}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        padding: '0.5rem 0', borderBottom: `1px solid ${C.border}`,
        cursor: 'pointer',
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
        background: C.primary + '25', overflow: 'hidden',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '0.85rem', fontWeight: 600, color: C.dark,
      }}>
        {p.foto_url
          ? <img src={`${API_BASE}${p.foto_url}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : (p.name || '?').charAt(0)
        }
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: '0.84rem', fontWeight: 600, color: C.darker, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p.name}
        </p>
        {sub && <p style={{ margin: 0, fontSize: '0.72rem', color: '#736b64' }}>{sub}</p>}
      </div>
      {p.whatsapp && (
        <a
          href={`https://wa.me/55${p.whatsapp.replace(/\D/g, '')}`}
          target="_blank" rel="noopener noreferrer"
          onClick={e => e.stopPropagation()}
          title="Enviar mensagem no WhatsApp"
          style={{ fontSize: '1rem', textDecoration: 'none', flexShrink: 0 }}
        >
          {icn(MessageCircle, 16)}
        </a>
      )}
    </div>
  );
}

// ── Fichas aguardando importação ──────────────────────────────────────────────
function AlertFicha({ f, onNavigate }) {
  const preenchidoEm = f.preenchido_em
    ? new Date(String(f.preenchido_em).replace(' ', 'T')).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
    : '';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '0.6rem',
      padding: '0.5rem 0', borderBottom: `1px solid ${C.border}`,
    }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0, background: C.yellow + '25', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem' }}>
        {icn(ClipboardList, 16)}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: '0.84rem', fontWeight: 600, color: C.darker, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {f.nome_paciente}
        </p>
        {preenchidoEm && <p style={{ margin: 0, fontSize: '0.72rem', color: '#736b64' }}>Preenchida em {preenchidoEm}</p>}
      </div>
      <button
        onClick={() => onNavigate('fichas-online')}
        style={{ fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.6rem', borderRadius: '20px', background: C.yellow + '20', color: C.yellow, border: `1px solid ${C.yellow}30`, cursor: 'pointer' }}
      >
        Importar
      </button>
    </div>
  );
}

function AlertBadge({ count, color }) {
  return (
    <span style={{
      marginLeft: '0.4rem', padding: '0.05rem 0.45rem', borderRadius: '999px',
      fontSize: '0.72rem', fontWeight: 600,
      background: color + '20', color,
    }}>
      {count}
    </span>
  );
}

// ── Custom tooltip para gráfico de barras ─────────────────────────────────────
function RevenueTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#ffffff', border: `1px solid ${C.border}`, borderRadius: '8px', padding: '0.6rem 0.85rem', fontSize: '0.82rem', boxShadow: '0 4px 12px rgba(0,0,0,.1)' }}>
      <p style={{ margin: '0 0 0.35rem', fontWeight: 600, color: C.darker }}>{emo(label)}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ margin: '0.1rem 0', color: p.color }}>
          {p.name}: {fmtBRL(p.value)}
        </p>
      ))}
    </div>
  );
}

// ── Main Dashboard ─────────────────────────────────────────────────────────────
// ── Inbox de pendências (Fase 4.1): o que precisa da minha atenção agora ─────
const PENDENCIA_LABEL = {
  anamnese_importar:        'Anamnese',
  pix_confirmar:            'Pagamento',
  consulta_sem_confirmacao: 'Confirmação',
  remarcacao:               'Remarcação',
  pre_consulta:             'Pré-consulta',
  reativacao:               'Reativação',
};

function PendenciasInbox({ onNavigate }) {
  const [pend, setPend]           = useState(null);
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    let ativo = true;
    pendenciasService.get()
      .then(res => { if (ativo && res.success) setPend(res.data); })
      .catch(() => { if (ativo) setPend({ total: 0, itens: [], erro: true }); });
    return () => { ativo = false; };
  }, []);

  if (!pend) return null; // carregando — não ocupa espaço

  if (pend.total === 0) {
    return (
      <div style={{ background: '#edf4ee', border: '1px solid #edf4ee', borderRadius: '12px', padding: '0.7rem 1rem', marginBottom: '1.25rem', fontSize: '0.86rem', color: '#2f5236', fontWeight: 600 }}>
        {ic(CheckCircle2)}Tudo em dia — nenhuma pendência no momento
      </div>
    );
  }

  const visiveis = expandido ? pend.itens : pend.itens.slice(0, 4);

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '1rem 1.15rem', marginBottom: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.65rem' }}>
        <span style={{ color: C.dark, display: 'flex' }}>{icn(Inbox, 18)}</span>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: C.darker, flex: 1 }}>
          Pendências
          <span style={{ marginLeft: '0.5rem', background: C.red, color: '#ffffff', borderRadius: '9999px', padding: '0.1rem 0.55rem', fontSize: '0.75rem', fontWeight: 700 }}>
            {pend.total}
          </span>
        </h2>
        {pend.itens.length > 4 && (
          <button
            onClick={() => setExpandido(e => !e)}
            style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}
          >
            {expandido ? 'Recolher ▲' : `Ver todas (${pend.itens.length}) ▼`}
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        {visiveis.map((item, i) => (
          <div
            key={i}
            onClick={() => onNavigate(item.view, item.patient_id || null)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: '0.55rem 0.7rem', borderRadius: '9px', cursor: 'pointer',
              background: '#fbf8f5', border: `1px solid ${C.border}`,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = '#f6f0ea'; }}
            onMouseLeave={e => { e.currentTarget.style.background = '#fbf8f5'; }}
          >
            <span style={{ fontSize: '1.05rem', flexShrink: 0 }}>{emo(item.icone)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: C.darker, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {emo(item.titulo)}
              </div>
              <div style={{ fontSize: '0.76rem', color: '#736b64', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {item.detalhe}
              </div>
            </div>
            <span style={{ fontSize: '0.68rem', fontWeight: 600, color: C.primary, background: `${C.primary}18`, padding: '0.18rem 0.5rem', borderRadius: '9999px', flexShrink: 0 }}>
              {emo(PENDENCIA_LABEL[item.tipo] || 'Ação')}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Alertas de saldo (OpenAI): avisa a nutri para adicionar créditos antes de parar.
function SaldoAlertas() {
  const [alertas, setAlertas] = useState([]);
  useEffect(() => {
    let ativo = true;
    saldoAlertasService.get()
      .then(res => { if (ativo && res.success) setAlertas(res.data.alertas || []); })
      .catch(() => {});
    return () => { ativo = false; };
  }, []);
  if (alertas.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1.25rem' }}>
      {alertas.map((a, i) => {
        const critico = a.nivel === 'critico';
        const bg = critico ? '#f8ebe9' : '#fbf8f5';
        const bd = critico ? '#f8ebe9' : '#e3c48a';
        const cor = critico ? '#a8483f' : '#8a5f1a';
        return (
          <div key={i} style={{ background: bg, border: `1px solid ${bd}`, borderRadius: '12px', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ fontSize: '1.2rem', flexShrink: 0 }}>{emo(a.icone || '⚠️')}</span>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.86rem', fontWeight: 700, color: cor }}>{emo(a.titulo)}</div>
              <div style={{ fontSize: '0.78rem', color: cor, opacity: 0.9 }}>{a.detalhe}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Leads a recuperar: quem conversou com o bot mas não agendou. Contato manual
// via wa.me (o disparo automático por template fica para a Fase 2).
function LeadsRecuperar({ onNavigate }) {
  const [data, setData]           = useState(null);
  const [expandido, setExpandido] = useState(false);

  useEffect(() => {
    let ativo = true;
    leadsService.get()
      .then(res => { if (ativo && res.success) setData(res.data); })
      .catch(() => { if (ativo) setData({ total: 0, leads: [] }); });
    return () => { ativo = false; };
  }, []);

  if (!data || data.total === 0) return null; // sem leads → não ocupa espaço

  const visiveis = expandido ? data.leads : data.leads.slice(0, 5);
  const quando = (dias) => dias <= 0 ? 'hoje' : dias === 1 ? 'ontem' : `há ${dias} dias`;

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: '14px', padding: '1rem 1.15rem', marginBottom: '1.25rem', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
        <span style={{ color: C.dark, display: 'flex' }}>{icn(MessageCircle, 18)}</span>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: C.darker, flex: 1 }}>
          Leads a recuperar
          <span style={{ marginLeft: '0.5rem', background: C.primary, color: '#ffffff', borderRadius: '9999px', padding: '0.1rem 0.55rem', fontSize: '0.75rem', fontWeight: 700 }}>
            {data.total}
          </span>
        </h2>
        {data.leads.length > 5 && (
          <button onClick={() => setExpandido(e => !e)}
            style={{ background: 'none', border: 'none', color: C.primary, cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600 }}>
            {expandido ? 'Recolher ▲' : `Ver todos (${data.leads.length}) ▼`}
          </button>
        )}
      </div>
      <p style={{ margin: '0 0 0.7rem', fontSize: '0.76rem', color: '#736b64' }}>
        Conversaram com a secretária virtual mas não agendaram. Chame no WhatsApp para reativar.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        {visiveis.map((l, i) => (
          <div key={i}
            style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.55rem 0.7rem', borderRadius: '9px', background: '#fbf8f5', border: `1px solid ${C.border}` }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: C.darker, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {emo(l.nome || `+${l.phone}`)}
                <span style={{ marginLeft: '0.5rem', fontSize: '0.68rem', fontWeight: 600, color: C.primary, background: `${C.primary}18`, padding: '0.12rem 0.45rem', borderRadius: '9999px' }}>
                  {l.etapa}
                </span>
              </div>
              <div style={{ fontSize: '0.76rem', color: '#736b64', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {quando(l.dias)} · {l.interacoes} msg{l.interacoes !== 1 ? 's' : ''}
                {l.last_message ? ` · "${l.last_message}"` : ''}
              </div>
            </div>
            {l.patient_id && (
              <button onClick={() => onNavigate('patient-detail', l.patient_id)}
                title="Ver ficha da paciente"
                style={{ flexShrink: 0, background: '#ffffff', color: C.dark, border: `1px solid ${C.border}`, borderRadius: '8px', padding: '0.35rem 0.55rem', cursor: 'pointer', fontSize: '0.75rem', fontWeight: 600 }}>
                {icn(User, 14)}
              </button>
            )}
            <a href={l.wa_link} target="_blank" rel="noopener noreferrer"
              title="Falar no WhatsApp"
              style={{ flexShrink: 0, background: '#6f9a77', color: '#ffffff', borderRadius: '8px', padding: '0.35rem 0.7rem', textDecoration: 'none', fontSize: '0.78rem', fontWeight: 600 }}>
              {ic(MessageCircle)}WhatsApp
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Dashboard({ onNavigate }) {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(true);
  const [isMobile, setIsMobile]       = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await dashboardService.getData();
      if (res.success) setData(res.data);
    } catch (e) {
      console.error('Dashboard load error', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const now     = new Date();
  const monthPT = now.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem', opacity: .4, color: C.dark }}>{icn(BarChart3, 40)}</div>
          <p style={{ color: '#736b64', fontSize: '0.9rem' }}>Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#736b64' }}>
        <p>Não foi possível carregar os dados. Verifique a conexão.</p>
        <button onClick={load} style={{ marginTop: '1rem', padding: '0.5rem 1.25rem', background: C.primary, color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer' }}>
          Tentar novamente
        </button>
      </div>
    );
  }

  const {
    financial_month: fin,
    appointments_month: appt,
    patients_overview: pat,
    no_return_list, birthdays_list, no_plan_list, fichas_aguardando,
    proxima_consulta, proximas_consultas, stats_30d,
    revenue_chart, objective_chart, active_patients_chart,
  } = data;

  const totalConsultas     = appt?.total ?? 0;
  const receitaProgress    = (fin?.meta_receita   || 0) > 0 ? ((fin.receita   / fin.meta_receita)   * 100) : 0;
  const consultasProgress  = (fin?.meta_consultas || 0) > 0 ? ((totalConsultas / fin.meta_consultas) * 100) : 0;

  const revenueData = (revenue_chart || []).map(r => ({ ...r, name: fmtMonth(r.month) }));
  const activeData  = (active_patients_chart || []).map(r => ({ ...r, name: fmtMonth(r.month) }));
  const objData     = (objective_chart || []).map(r => ({ ...r, name: OBJ_LABELS[r.objetivo] || r.objetivo }));

  return (
    <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 0.25rem' }}>

      {/* ── Cabeçalho ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '2.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ margin: '0 0 0.2rem', fontSize: '2.125rem', fontWeight: 500, letterSpacing: '-0.01em', color: C.darker }}>Dashboard</h1>
          <p style={{ margin: 0, color: '#736b64', fontSize: '0.9375rem', textTransform: 'capitalize' }}>{monthPT}</p>
        </div>
        <div style={isMobile
          ? { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', width: '100%' }
          : { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}
        >
          <button
            onClick={() => onNavigate('patient-new')}
            style={{ padding: '0.55rem 1.1rem', background: C.primary, color: '#ffffff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem', gridColumn: isMobile ? '1 / -1' : undefined }}
          >
            {ic(Plus)}Novo Paciente
          </button>
          <button
            onClick={() => onNavigate('agenda')}
            style={{ padding: '0.55rem 1.1rem', background: '#ffffff', color: C.dark, border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
          >
            {ic(Calendar)}Agenda
          </button>
        </div>
      </div>

      {/* ── Alertas de saldo (OpenAI) ── */}
      <SaldoAlertas />

      {/* ── Inbox de pendências (Fase 4.1) ── */}
      <PendenciasInbox onNavigate={onNavigate} />

      {/* ── Leads a recuperar (reativação manual via WhatsApp) ── */}
      <LeadsRecuperar onNavigate={onNavigate} />

      {/* ── Bloco: Próxima consulta + Stats 30d + Meta ── */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 280px', gap: '0.75rem', marginBottom: '0.75rem', alignItems: 'start' }}>
        <ProximaConsultaCard consulta={proxima_consulta} onNavigate={onNavigate} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Stats30d stats={stats_30d} />
          <MetaMes fin={fin} appt={appt} />
        </div>
      </div>

      {/* ── Próximas consultas ── */}
      {proximas_consultas?.length > 0 && (
        <div style={{ marginBottom: '1.5rem' }}>
          <ProximasConsultasList consultas={proximas_consultas} onNavigate={onNavigate} />
        </div>
      )}

      {/* ── Linha 2: Consultas ── */}
      <SectionLabel>Consultas do Mês</SectionLabel>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.75rem', flexWrap: 'wrap' }}>
        <SummaryCard
          icon={icn(Building2, 22)} label="Presenciais" value={appt?.presenciais ?? 0} color={C.primary}
          sub="Realizadas no mês"
        />
        <SummaryCard
          icon={icn(Laptop, 22)} label="Online" value={appt?.online ?? 0} color={C.blue}
          sub="Realizadas no mês"
        />
        <SummaryCard
          icon={icn(Calendar, 22)} label="Agendadas hoje" value={appt?.hoje ?? 0} color={C.purple}
          sub={totalConsultas > 0 ? `${totalConsultas} no mês` : undefined}
          progress={consultasProgress}
        />
        <SummaryCard
          icon={icn(AlertCircle, 22)} label="Sem retorno +90d" value={pat?.sem_retorno_count ?? 0} color={C.red}
          sub="Necessitam contato"
        />
      </div>

      {/* ── Gráficos ── */}
      <SectionLabel>Visão Geral</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1rem', marginBottom: '1.75rem' }}>

        <Card>
          <p style={{ margin: '0 0 1rem', fontWeight: 600, color: C.darker, fontSize: '0.88rem' }}>
            {ic(BarChart3)}Receita — Últimos 6 Meses
          </p>
          <ResponsiveContainer width="100%" height={195}>
            <BarChart data={revenueData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ebe3da" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#a39b93' }} />
              <YAxis tick={{ fontSize: 11, fill: '#a39b93' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
              <Tooltip content={<RevenueTooltip />} />
              <Legend iconSize={9} wrapperStyle={{ fontSize: '0.75rem' }} />
              <Bar dataKey="presencial" name="Presencial" fill={C.primary} radius={[3,3,0,0]} />
              <Bar dataKey="online"     name="Online"     fill={C.blue}    radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <p style={{ margin: '0 0 0.5rem', fontWeight: 600, color: C.darker, fontSize: '0.88rem' }}>
            {ic(PieIcon)}Pacientes por Objetivo
          </p>
          {objData.length === 0 ? (
            <div style={{ height: 195, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ color: '#d9d0c6', fontSize: '0.82rem' }}>Sem dados ainda</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={195}>
              <PieChart>
                <Pie data={objData} dataKey="total" nameKey="name" cx="50%" cy="50%" outerRadius={72} innerRadius={28}>
                  {objData.map((_, i) => <Cell key={i} fill={OBJ_COLORS[i % OBJ_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v + ' pacientes', n]} />
                <Legend iconSize={9} wrapperStyle={{ fontSize: '0.74rem' }} iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card>
          <p style={{ margin: '0 0 1rem', fontWeight: 600, color: C.darker, fontSize: '0.88rem' }}>
            {ic(TrendingUp)}Pacientes Ativos — 6 Meses
          </p>
          <ResponsiveContainer width="100%" height={195}>
            <LineChart data={activeData} margin={{ top: 4, right: 4, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ebe3da" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#a39b93' }} />
              <YAxis tick={{ fontSize: 11, fill: '#a39b93' }} />
              <Tooltip formatter={(v) => [v, 'Pacientes']} />
              <Line type="monotone" dataKey="total" stroke={C.primary} strokeWidth={2.5}
                dot={{ fill: C.primary, r: 4, strokeWidth: 0 }} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* ── Alertas ── */}
      <SectionLabel>Alertas Inteligentes</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1.75rem' }}>

        {/* Sem retorno +90 dias */}
        <Card>
          <p style={{ margin: '0 0 0.6rem', fontWeight: 600, color: C.red, fontSize: '0.88rem' }}>
            {ic(AlertCircle)}Sem Retorno há +90 dias
            <AlertBadge count={no_return_list?.length ?? 0} color={C.red} />
          </p>
          {!no_return_list?.length ? (
            <p style={{ color: '#d9d0c6', fontSize: '0.82rem', padding: '0.5rem 0', textAlign: 'center' }}>Nenhuma pendência</p>
          ) : (
            no_return_list.slice(0, 6).map(p => (
              <AlertPatient key={p.id} p={p}
                sub={p.dias_sem_retorno != null ? `${p.dias_sem_retorno} dias sem consulta` : 'Nunca consultou'}
                onNavigate={onNavigate}
              />
            ))
          )}
        </Card>

        {/* Fichas aguardando importação */}
        <Card>
          <p style={{ margin: '0 0 0.6rem', fontWeight: 600, color: C.yellow, fontSize: '0.88rem' }}>
            {ic(ClipboardList)}Fichas Aguardando Importação
            <AlertBadge count={fichas_aguardando?.length ?? 0} color={C.yellow} />
          </p>
          {!fichas_aguardando?.length ? (
            <p style={{ color: '#d9d0c6', fontSize: '0.82rem', padding: '0.5rem 0', textAlign: 'center' }}>Nenhuma ficha pendente</p>
          ) : (
            fichas_aguardando.slice(0, 6).map((f, i) => (
              <AlertFicha key={f.token || i} f={f} onNavigate={onNavigate} />
            ))
          )}
        </Card>

        {/* Aniversariantes */}
        <Card>
          <p style={{ margin: '0 0 0.6rem', fontWeight: 600, color: '#b8c7d8', fontSize: '0.88rem' }}>
            {ic(Cake)}Aniversariantes do Mês
            <AlertBadge count={birthdays_list?.length ?? 0} color="#b8c7d8" />
          </p>
          {!birthdays_list?.length ? (
            <p style={{ color: '#d9d0c6', fontSize: '0.82rem', padding: '0.5rem 0', textAlign: 'center' }}>Nenhum aniversariante</p>
          ) : (
            birthdays_list.slice(0, 6).map(p => (
              <AlertPatient key={p.id} p={p} sub={`Dia ${p.dia}`} onNavigate={onNavigate} />
            ))
          )}
        </Card>

        {/* Sem plano alimentar */}
        <Card>
          <p style={{ margin: '0 0 0.6rem', fontWeight: 600, color: C.purple, fontSize: '0.88rem' }}>
            {ic(ClipboardList)}Sem Plano Alimentar
            <AlertBadge count={no_plan_list?.length ?? 0} color={C.purple} />
          </p>
          {!no_plan_list?.length ? (
            <p style={{ color: '#d9d0c6', fontSize: '0.82rem', padding: '0.5rem 0', textAlign: 'center' }}>Todos com plano ativo</p>
          ) : (
            no_plan_list.slice(0, 6).map(p => (
              <AlertPatient key={p.id} p={p} onNavigate={onNavigate} />
            ))
          )}
        </Card>
      </div>

      {/* ── Ações rápidas ── */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', paddingBottom: '1rem' }}>
        <button
          onClick={() => onNavigate('patients')}
          style={{ padding: '0.55rem 1.1rem', background: '#ffffff', color: C.dark, border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
        >
          {ic(Users)}Todos os pacientes ({pat?.total_ativos ?? 0})
        </button>
        <button
          onClick={() => onNavigate('relatorios')}
          style={{ padding: '0.55rem 1.1rem', background: '#ffffff', color: C.dark, border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
        >
          {ic(BarChart3)}Relatórios
        </button>
        <button
          onClick={() => onNavigate('fichas-online')}
          style={{ padding: '0.55rem 1.1rem', background: '#ffffff', color: C.dark, border: `1px solid ${C.border}`, borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
        >
          {ic(ClipboardList)}Fichas Online
        </button>
      </div>

    </div>
  );
}
